import { Link } from "react-router-dom";

const contactEmail = "management@potimusproperties.com";

function ContactLink() {
  return <a href={`mailto:${contactEmail}`}>{contactEmail}</a>;
}

export function PrivacyPolicy() {
  return (
    <article className="content-page legal-page">
      <p className="eyebrow">Privacy</p>
      <h1>Privacy Policy</h1>
      <p className="legal-meta">Last updated August 17, 2026</p>
      <p className="lede">
        Potimus Properties LLC ("Potimus Properties," "we," or "us") uses personal information to
        provide property-management services, communicate with residents, maintain accurate records,
        and respond to maintenance and safety matters.
      </p>

      <section className="legal-section" aria-labelledby="privacy-information">
        <h2 id="privacy-information">Information we collect</h2>
        <p>Depending on your relationship with us, we may collect:</p>
        <ul>
          <li>Contact information, including your name, email address, and telephone number.</li>
          <li>Tenancy, lease, payment, deposit, receipt, and property-related records.</li>
          <li>
            Communications with us, including maintenance requests, photographs or videos you
            submit, call recordings and transcripts when separately disclosed and consented to, and
            records of messaging consent or opt-out requests.
          </li>
          <li>
            Technical information needed to operate and secure our website and resident services.
          </li>
        </ul>
      </section>

      <section className="legal-section" aria-labelledby="privacy-use">
        <h2 id="privacy-use">How we use information</h2>
        <p>We use personal information to:</p>
        <ul>
          <li>Manage properties, tenancies, leases, payments, receipts, and resident records.</li>
          <li>
            Respond to questions, coordinate maintenance and access, provide status updates, and
            communicate safety information.
          </li>
          <li>Verify consent, honor communication preferences, and process opt-out requests.</li>
          <li>Protect our residents, properties, services, and legal rights.</li>
          <li>Meet recordkeeping, accounting, insurance, and legal obligations.</li>
        </ul>
      </section>

      <section className="legal-section" aria-labelledby="privacy-mobile">
        <h2 id="privacy-mobile">Mobile information and messaging consent</h2>
        <p>
          We do not sell or rent mobile information. We do not share mobile numbers, text-messaging
          originator opt-in data, or messaging consent with third parties or affiliates for their
          marketing or promotional purposes. We may provide information to service providers that
          help us deliver and support our property-management communications, but only for those
          operational purposes, or when disclosure is required by law.
        </p>
        <p>
          Our messaging provider may transmit, collect, and monitor message content to detect spam,
          fraud, security threats, and violations of messaging rules.
        </p>
        <p>
          The Potimus Properties resident messaging program is non-marketing. Message frequency
          varies based on resident requests, active maintenance matters, scheduling, receipts, and
          safety needs. Message and data rates may apply. See our{" "}
          <Link to="/messaging">Messaging Program</Link> and{" "}
          <Link to="/terms">Terms of Service</Link> for additional details.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="privacy-sharing">
        <h2 id="privacy-sharing">When information may be disclosed</h2>
        <p>We may disclose information:</p>
        <ul>
          <li>
            To service providers supporting hosting, communications, document storage, maintenance,
            accounting, security, or other property-management operations under appropriate
            restrictions.
          </li>
          <li>
            To contractors or vendors when reasonably necessary to inspect, schedule, or complete
            authorized work.
          </li>
          <li>To comply with law, legal process, insurance requirements, or safety obligations.</li>
          <li>With your direction or consent.</li>
        </ul>
        <p>
          Resident information is not made public. Occupied-property pages do not display exact
          street addresses, resident identities, or resident-submitted maintenance media.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="privacy-retention">
        <h2 id="privacy-retention">Retention and security</h2>
        <p>
          We retain property-management records for the period reasonably needed for operations and
          applicable recordkeeping obligations. Records subject to a legal hold or other required
          retention are not deleted until the hold or requirement ends. We use administrative,
          technical, and physical safeguards designed to protect personal information, but no system
          can guarantee absolute security.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="privacy-choices">
        <h2 id="privacy-choices">Your choices</h2>
        <p>
          You may ask to review or correct your contact information, change your routine
          communication preference, or ask a privacy question by contacting us. Texting STOP opts
          the mobile number out of further text messages, subject to a final confirmation message.
          Opting out of text messages does not prevent you from contacting us through another
          available channel.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="privacy-contact">
        <h2 id="privacy-contact">Contact us</h2>
        <p>
          Potimus Properties LLC
          <br />
          Seaside, Oregon
          <br />
          Email: <ContactLink />
        </p>
      </section>

      <p className="legal-review-note">
        We may update this policy as our services or legal obligations change. The effective date
        above identifies the current version.
      </p>
    </article>
  );
}

export function TermsOfService() {
  return (
    <article className="content-page legal-page">
      <p className="eyebrow">Legal</p>
      <h1>Terms of Service</h1>
      <p className="legal-meta">Last updated August 17, 2026</p>
      <p className="lede">
        These terms govern use of the Potimus Properties LLC public website and resident
        communication services. A signed lease and applicable law control if they conflict with
        these website terms.
      </p>

      <section className="legal-section" aria-labelledby="terms-website">
        <h2 id="terms-website">Website information</h2>
        <p>
          This website provides general information about Potimus Properties and access to resident
          support. Public property information is limited to owner-approved content. Availability,
          pricing, and property details may change and are not a lease offer or guarantee.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="terms-emergency">
        <h2 id="terms-emergency">Emergencies</h2>
        <p>
          The website, email, text messaging, and automated calling services are not substitutes for
          emergency services. For fire, suspected gas leaks, immediate danger, water near
          electricity, suspected break-ins, or another immediate threat, call 911 or the appropriate
          utility emergency service first.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="terms-messaging">
        <h2 id="terms-messaging">Resident messaging terms</h2>
        <p>
          Potimus Properties sends non-marketing property-management messages only to recipients who
          have provided consent or requested a specific communication. A request for a single reply
          authorizes only that exchange unless the recipient separately consents to recurring
          messages. Messages may include maintenance intake and updates, scheduling and access
          coordination, payment or receipt confirmations, resident-service replies, safety notices,
          and requested callbacks.
        </p>
        <ul>
          <li>Consent to text messaging is voluntary and is not a condition of renting a home.</li>
          <li>
            Message frequency varies based on resident requests, active cases, scheduling, receipts,
            and safety needs.
          </li>
          <li>Message and data rates may apply.</li>
          <li>Reply STOP to opt out of text messages. Reply HELP for help.</li>
          <li>
            Carriers are not liable for delayed or undelivered messages. Message delivery is not
            guaranteed.
          </li>
        </ul>
        <p>
          If you change, transfer, or discontinue your mobile number, please tell us promptly so
          messages are not sent to a later user of that number.
        </p>
        <p>
          See the <Link to="/messaging">Messaging Program</Link> for the complete opt-in, opt-out,
          and support description. Our <Link to="/privacy">Privacy Policy</Link> explains how we
          handle personal and mobile information.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="terms-acceptable-use">
        <h2 id="terms-acceptable-use">Acceptable use</h2>
        <p>
          Do not attempt to access another person&apos;s records, interfere with the website, upload
          malicious material, impersonate another person, or use resident communication channels for
          unlawful or abusive conduct.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="terms-availability">
        <h2 id="terms-availability">Availability and changes</h2>
        <p>
          We may maintain, update, restrict, or discontinue website features. We work to provide
          reliable communications but cannot promise uninterrupted availability or immediate carrier
          delivery. Material property-management issues remain subject to the lease, documented
          approvals, and applicable law.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="terms-contact">
        <h2 id="terms-contact">Questions</h2>
        <p>
          Contact Potimus Properties LLC at <ContactLink />.
        </p>
      </section>
    </article>
  );
}

export function MessagingProgram() {
  return (
    <article className="content-page legal-page">
      <p className="eyebrow">Resident communications</p>
      <h1>Messaging Program</h1>
      <p className="lede">
        Potimus Properties uses text messages for requested, non-marketing property-management
        communications with residents and other authorized recipients.
      </p>

      <section className="legal-section" aria-labelledby="messaging-content">
        <h2 id="messaging-content">What messages include</h2>
        <p>
          Messages may concern maintenance intake and status, appointment or access coordination,
          replies to resident questions, payment and receipt confirmations, safety information, and
          requested callbacks. We do not use this program for marketing.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="messaging-consent">
        <h2 id="messaging-consent">How consent works</h2>
        <p>
          A resident or authorized recipient may opt in through documented written consent or a
          separately presented consent choice during resident onboarding. When keyword enrollment is
          available, texting START also opts the mobile number in. A request for a single text reply
          or callback authorizes only that requested exchange; it does not by itself enroll the
          number in recurring messages. Consent is specific to Potimus Properties and is not
          transferred to another business.
        </p>
        <p>
          Consent is optional, is not a condition of renting a home, and may be withdrawn at any
          time. A person who declines text messaging may continue to use another available contact
          method.
        </p>
        <p>
          A blank, printable <Link to="/sms-consent">SMS Consent Form</Link> is available for people
          who prefer to document optional consent on paper.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="messaging-frequency">
        <h2 id="messaging-frequency">Frequency and charges</h2>
        <p>
          Message frequency varies based on resident requests, active maintenance matters,
          scheduling, receipts, and safety needs. Message and data rates may apply under your mobile
          plan.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="messaging-stop-help">
        <h2 id="messaging-stop-help">STOP and HELP</h2>
        <p>
          Reply STOP to opt out of text messages. We may send one final message confirming the
          opt-out. Reply START to opt back in when keyword enrollment is available. Reply HELP for
          help, or email <ContactLink />. Carriers are not liable for delayed or undelivered
          messages.
        </p>
      </section>

      <section className="legal-section" aria-labelledby="messaging-privacy">
        <h2 id="messaging-privacy">Privacy</h2>
        <p>
          Mobile information and messaging consent are not sold, rented, or shared with third
          parties or affiliates for marketing or promotional purposes. Review our{" "}
          <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms of Service</Link>{" "}
          for more information.
        </p>
      </section>
    </article>
  );
}

export function SmsConsentForm() {
  return (
    <article className="content-page legal-page sms-consent-page">
      <div className="consent-actions">
        <p className="eyebrow">Blank public form</p>
        <button className="button button-small" onClick={() => window.print()} type="button">
          Print blank form
        </button>
      </div>
      <h1>Optional SMS Consent Form</h1>
      <p className="legal-meta">Potimus Properties Resident Messaging Program</p>
      <p className="consent-instructions">
        This blank form does not submit or store information online. Print it, complete it by hand,
        and return the signed form directly to Potimus Properties LLC.
      </p>

      <form className="sms-consent-form" onSubmit={(event) => event.preventDefault()}>
        <section aria-labelledby="consent-recipient">
          <h2 id="consent-recipient">Recipient information</h2>
          <div className="consent-fields">
            <label>
              Name
              <input autoComplete="name" name="recipient-name" type="text" />
            </label>
            <label>
              Mobile number
              <input autoComplete="tel" inputMode="tel" name="mobile-number" type="tel" />
            </label>
          </div>
        </section>

        <section aria-labelledby="consent-program">
          <h2 id="consent-program">Program description and optional consent</h2>
          <p>
            The Potimus Properties Resident Messaging Program sends recurring, non-marketing
            property-management text messages from Potimus Properties LLC. Messages may include
            maintenance intake and status updates, scheduling or access coordination, replies to
            resident questions, payment and receipt confirmations, safety information, and requested
            callbacks. Messages may be sent using an automated system.
          </p>
          <label className="paper-consent-choice">
            <input name="optional-sms-consent" type="checkbox" />
            <span>
              I voluntarily authorize Potimus Properties LLC to send recurring text messages for the
              program described above to the mobile number I provided. I confirm that I am the
              subscriber or customary user of that number.
            </span>
          </label>
          <p>
            Message frequency varies based on resident requests, active maintenance matters,
            scheduling, receipts, and safety needs. Message and data rates may apply. Reply HELP for
            help. Reply STOP to opt out at any time.
          </p>
          <p>
            Consent is optional and is not a condition of renting a home, receiving property
            management services, or receiving other tenancy services. If I do not consent, I may use
            another available contact method.
          </p>
          <p>
            Privacy Policy:{" "}
            <a href="https://potimusproperties.com/privacy">
              https://potimusproperties.com/privacy
            </a>
            <br />
            Terms of Service:{" "}
            <a href="https://potimusproperties.com/terms">https://potimusproperties.com/terms</a>
          </p>
        </section>

        <section aria-labelledby="consent-signature">
          <h2 id="consent-signature">Signature</h2>
          <div className="consent-fields signature-fields">
            <label>
              Signature
              <input name="signature" type="text" />
            </label>
            <label>
              Date
              <input name="signature-date" type="text" />
            </label>
            <label>
              Printed name
              <input name="printed-name" type="text" />
            </label>
            <label>
              Initials
              <input name="initials" type="text" />
            </label>
          </div>
        </section>

        <p className="consent-contact">
          Potimus Properties LLC · Seaside, Oregon · <ContactLink />
        </p>
      </form>
    </article>
  );
}
